import { AfterViewInit, Component, Input, OnChanges, SimpleChanges, ViewChild } from "@angular/core";
import { MatTableDataSource } from "@angular/material/table";
import { MatSort } from "@angular/material/sort";
import { CurrencyPipe, DecimalPipe } from "@angular/common";
import { MaterialModule } from "../../material/material.module";
import { EDGE_TYPE, getInsights, Linqset, LinqsetNode, NODE_SUPERTYPE, NODE_TYPE } from "../../common/linqset-model";
import { DEFAULT_CURRENCY, Organisation } from "../../common/organisation";
import { UIComponentsModule } from "../../ui-components/ui-components.module";

class Interface {

    constructor(public from:string, public to:string, public role:string, public action:string, public information:string, public asset:string, public time:number, public cost:number, public model:string) {}
}

@Component({
    selector: 'app-system-interfaces',
    templateUrl: './system-interfaces.html',
    styleUrls: ['./system-interfaces.css'],
    standalone: true,
    imports: [MaterialModule, DecimalPipe, CurrencyPipe, UIComponentsModule]
})
export class SystemInterfacesComponent implements AfterViewInit, OnChanges {
    @Input() organisation:Organisation
    @Input() linqset:Linqset

    interfaceData:MatTableDataSource<Interface> = new MatTableDataSource<Interface>()
    systemCount = 0

    // the sort header is inside the table, which only exists once there are rows
    @ViewChild(MatSort) set sort(sort:MatSort) {
        if (sort) this.interfaceData.sort = sort
    }

    columns = ['from', 'to', 'role', 'action', 'information', 'asset', 'time', 'cost', 'model'];
    headings = ['From system', 'To', 'Role', 'Action', 'Information', 'Asset', 'Hours', 'Cost', 'Model'];

    // header icons, one per column; the figures columns have none
    icons:(NODE_TYPE|undefined)[] = [
        NODE_TYPE.SYSTEM,
        NODE_TYPE.SYSTEM,
        NODE_TYPE.ROLE,
        NODE_TYPE.ACTION,
        NODE_TYPE.INFORMATION,
        NODE_TYPE.ASSET,
        undefined,
        undefined,
        NODE_TYPE.MODEL
    ]

    get currencyCode() {
        return this.organisation?.currencyCode || DEFAULT_CURRENCY
    }

    ngAfterViewInit(): void {
        if (!this.interfaceData.data.length) this.populateData()
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['linqset'] && this.linqset) this.populateData()
    }

    populateData() {
        const rows = this.createSystemInterfacesRows()
        this.interfaceData.data = rows
        this.systemCount = new Set(rows.map(r => r.from)).size
    }

    private createSystemInterfacesRows() {
        const rows:Interface[] = []
        const models:any = {}
        const seen = new Set<string>()
        if (!this.linqset) return rows

        const add = (row:Interface|undefined) => {
            if (!row) return
            // the same interface can be reached through more than one edge
            const key = [row.from, row.to, row.role, row.action, row.information, row.model].join('|')
            if (seen.has(key)) return
            seen.add(key)
            rows.push(row)
        }

        this.linqset.nodes.filter(n => n.superType === NODE_SUPERTYPE.SYSTEM).forEach(s => {
            // information this system supplies
            this.linqset.edges.filter(e => e.type === EDGE_TYPE.SUPPLY && e.sourceKey === s._key).forEach(e => {
                // actions that information supports
                this.linqset.edges.filter(e2 => e2.sourceKey === e.targetKey && e2.type == EDGE_TYPE.SUPPORT).forEach(e2 => {
                    // other systems automating those actions
                    this.linqset.edges.filter(e3 => e3.targetKey === e2.targetKey && e3.sourceKey !== s._key && e3.type == EDGE_TYPE.AUTOMATE).forEach(e3 => {
                        add(this.createRow(models, s, e3.sourceKey, e3.targetKey, e.targetKey))
                    })
                    // systems used by the roles performing those actions
                    this.linqset.edges.filter(e3 => e3.targetKey === e2.targetKey && e3.type == EDGE_TYPE.PERFORM).forEach(e3 => {
                        this.linqset.edges.filter(e4 => e4.targetVisualisationKey === e3.sourceVisualisationKey && e4.sourceKey !== s._key && e4.type == EDGE_TYPE.USEBY).forEach(e4 => {
                            add(this.createRow(models, s, e4.sourceKey, e3.targetKey, e.targetKey, e3.sourceKey))
                        })
                    })
                })
            })
        })
        return rows
    }

    private createRow(models:any, system:LinqsetNode, toKey:string, actionKey:string, infoKey:string, roleKey?:string):Interface|undefined {
        const action = this.linqset.getNode(actionKey)
        if (!action) return undefined

        const to = this.linqset.getNode(toKey)
        const insights = getInsights(action, this.linqset)
        const info = this.linqset.getNode(infoKey)
        const asset = info?.informationAssetKey && this.organisation.informationAssets?.find(ia => ia._key === info.informationAssetKey)
        const role = roleKey && this.linqset.getNode(roleKey)

        if (!models[action.modelKey]) models[action.modelKey] = this.organisation.models.find(m => m._key === action.modelKey)

        return new Interface(system.name||'', to?.name||'', role?.name||'', action.name||'', info?.name||'', asset?.name||'', insights?.hours, insights?.allocatedCostsByType?.total, models[action.modelKey]?.name || '')
    }
}
